import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ShieldAlert,
    UserX,
    PlaneTakeoff,
    Lock,
    Globe,
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    Search,
    ChevronLeft,
    ChevronRight,
    KeyRound,
    UserCheck,
    Clock,
    FileText,
    ExternalLink,
    Shield
} from 'lucide-react';
import { backendURL } from '../config';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

export default function EntraIdentityProtection() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('travel'); // 'travel' | 'users' | 'signins' | 'audits'
    const [signInSearch, setSignInSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState(null);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/identity/protection`);
            if (res.data && res.data.success) {
                setData(res.data);
            }
        } catch (err) {
            console.error("Fetch identity protection error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleRemediate = async (user, action = "Revoke Tokens & Enforce MFA") => {
        try {
            const res = await axios.post(`${backendURL}/api/soc/identity/remediate-user`, { user, action });
            setToast({
                success: true,
                message: res.data.message || `Remediation action successfully executed on ${user}.`
            });
            setTimeout(() => setToast(null), 4500);
        } catch (err) {
            setToast({
                success: false,
                message: `Failed to remediate ${user}: ${err.message}`
            });
            setTimeout(() => setToast(null), 4500);
        }
    };

    const kpis = data?.kpis || {
        totalRiskyUsers: 0,
        highRiskCount: 0,
        impossibleTravelCount: 0,
        totalSignIns: 0,
        auditEventsCount: 0
    };

    const filteredSignIns = (data?.signIns || []).filter(s => {
        const matchesSearch = !signInSearch ||
            (s.user || '').toLowerCase().includes(signInSearch.toLowerCase()) ||
            (s.ip || '').includes(signInSearch) ||
            (s.location || '').toLowerCase().includes(signInSearch.toLowerCase());

        const isFail = s.type?.includes("Failed") || s.failures?.includes("Failed") || s.raw?.status?.errorCode !== 0;
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'failed' && isFail) ||
            (statusFilter === 'success' && !isFail);

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Action Feedback Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 text-sm font-medium border animate-in slide-in-from-top-4 duration-200 ${
                    toast.success 
                        ? 'bg-emerald-950/90 border-emerald-500/40 text-emerald-300' 
                        : 'bg-rose-950/90 border-rose-500/40 text-rose-300'
                }`}>
                    {toast.success ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* Top Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-purple-600/10 border border-purple-500/20 text-purple-400">
                        <KeyRound className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                Microsoft Entra ID Identity Protection
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">
                                LIVE IDENTITY SENSORS
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Continuous Access Evaluation (CAE)
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30 font-mono flex items-center gap-1">
                                <Shield className="w-3 h-3 text-sky-400" />
                                STRICT READ-ONLY MODE
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Autonomous Threat Detection: Risky Users, Impossible Travel Engine, Sign-in Logs & Directory Audit Trail
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={fetchData}
                        disabled={refreshing}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-purple-400 ${refreshing ? 'animate-spin' : ''}`} />
                        <span>{refreshing ? 'Syncing...' : 'Sync Identity'}</span>
                    </button>
                    <a
                        href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/IdentityProtectionMenuBlade/~/Overview"
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-purple-600/20"
                    >
                        <span>Open Entra Admin Center</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Risky Users
                        <UserX className="w-3.5 h-3.5 text-amber-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-amber-400">
                        {kpis.totalRiskyUsers}
                    </div>
                    <span className="text-[10px] text-amber-400/80 font-mono mt-1">Elevated Risk Profile</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        High Risk Identities
                        <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-rose-400">
                        {kpis.highRiskCount}
                    </div>
                    <span className="text-[10px] text-rose-400/80 font-mono mt-1">Critical Remediation Needed</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Impossible Travel
                        <PlaneTakeoff className="w-3.5 h-3.5 text-sky-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-sky-400">
                        {kpis.impossibleTravelCount}
                    </div>
                    <span className="text-[10px] text-sky-400/80 font-mono mt-1">Geographic Velocity Alerts</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Sign-in Telemetry
                        <Lock className="w-3.5 h-3.5 text-blue-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-slate-100">
                        {kpis.totalSignIns}
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono mt-1">Interactive Logins Monitored</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Directory Audits
                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-emerald-400">
                        {kpis.auditEventsCount}
                    </div>
                    <span className="text-[10px] text-emerald-400/80 font-mono mt-1">Admin Security Actions</span>
                </div>
            </div>

            {/* Impossible Travel Featured Alert Banner */}
            {(data?.impossibleTravelEvents || []).length > 0 && (
                <div className="p-4 rounded-2xl bg-gradient-to-r from-rose-950/40 via-purple-950/30 to-[#0d1424] border border-rose-500/30 shadow-xl space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-rose-500/20 text-rose-400 border border-rose-500/30 animate-pulse">
                                <PlaneTakeoff className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black uppercase text-rose-300 font-mono">
                                        Impossible Travel Security Alert Detected
                                    </span>
                                    <span className="px-1.5 py-0.2 rounded text-[9px] font-mono font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                        VELOCITY &gt; 900 KM/H
                                    </span>
                                </div>
                                <p className="text-xs text-slate-300 mt-0.5">
                                    {data.impossibleTravelEvents[0].details}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col items-start md:items-end gap-1">
                            <button
                                onClick={() => handleRemediate(data.impossibleTravelEvents[0].user, "Revoke Sessions & Reset Password")}
                                className="px-3.5 py-1.5 rounded-xl bg-rose-600/90 hover:bg-rose-500 text-white text-xs font-bold font-mono transition-all shadow-lg shadow-rose-600/30 flex items-center gap-1.5 self-start md:self-center"
                            >
                                <Lock className="w-3.5 h-3.5" />
                                <span>1-Click Revoke &amp; Lock User</span>
                            </button>
                            <span className="text-[9.5px] font-mono text-emerald-400/90 flex items-center gap-1">
                                <Shield className="w-2.5 h-2.5 text-emerald-400" />
                                <span>Strict Read-Only Mode (Zero Mutating Changes to Azure)</span>
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-white/[0.06] text-xs font-mono">
                        <div className="bg-black/30 p-2.5 rounded-xl border border-white/[0.04]">
                            <span className="text-[10px] text-slate-500 block uppercase">Identity Account</span>
                            <strong className="text-slate-100">{data.impossibleTravelEvents[0].user}</strong>
                        </div>
                        <div className="bg-black/30 p-2.5 rounded-xl border border-white/[0.04]">
                            <span className="text-[10px] text-slate-500 block uppercase">Origin Flight Path</span>
                            <span className="text-slate-300">
                                {data.impossibleTravelEvents[0].originLocation} → {data.impossibleTravelEvents[0].targetLocation}
                            </span>
                        </div>
                        <div className="bg-black/30 p-2.5 rounded-xl border border-white/[0.04]">
                            <span className="text-[10px] text-slate-500 block uppercase">Target IP (AbuseIPDB &amp; VT)</span>
                            <AbuseIPDBPopover ip={data.impossibleTravelEvents[0].targetIp} showBadge={true} className="text-blue-400 hover:underline">
                                {data.impossibleTravelEvents[0].targetIp}
                            </AbuseIPDBPopover>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 border-b border-white/[0.08] pb-1 text-xs">
                {[
                    { id: 'travel', label: 'Impossible Travel Engine', count: (data?.impossibleTravelEvents || []).length, icon: PlaneTakeoff },
                    { id: 'users', label: 'Risky Users Roster', count: (data?.riskyUsers || []).length, icon: UserX },
                    { id: 'signins', label: 'Interactive Sign-in Logs', count: (data?.signIns || []).length, icon: Lock },
                    { id: 'audits', label: 'Directory Security Audits', count: (data?.auditTrail || []).length, icon: FileText }
                ].map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2.5 rounded-t-xl font-bold transition-all flex items-center gap-2 ${
                                activeTab === tab.id
                                    ? 'bg-[#0d1424] text-purple-400 border-t-2 border-purple-500'
                                    : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            <span>{tab.label}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-white/[0.06] text-slate-400">
                                {tab.count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* TAB 1: IMPOSSIBLE TRAVEL ENGINE */}
            {activeTab === 'travel' && (
                <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden space-y-4 p-4">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-300 font-mono">
                            Impossible Travel &amp; Geographic Velocity Detections
                        </span>
                        <span className="text-[10px] font-mono text-slate-500">Autonomous Entra ID Protection Matrix</span>
                    </div>

                    <div className="space-y-3">
                        {(data?.impossibleTravelEvents || []).length === 0 ? (
                            <div className="p-8 rounded-xl bg-black/40 border border-white/[0.04] text-center">
                                <PlaneTakeoff className="w-8 h-8 text-slate-500 mx-auto mb-2 opacity-50" />
                                <h4 className="text-sm font-semibold text-slate-300">No Impossible Travel Anomalies</h4>
                                <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                                    All interactive authentication sessions within the audit window originate from compliant geographic velocities (&lt;900 km/h).
                                </p>
                            </div>
                        ) : (
                            (data?.impossibleTravelEvents || []).map((event, idx) => (
                            <div key={idx} className="p-4 rounded-xl bg-black/40 border border-white/[0.04] flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                            {event.riskType}
                                        </span>
                                        <strong className="text-sm text-slate-100">{event.user}</strong>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        {event.details}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4 text-[10px] text-slate-500 font-mono pt-1">
                                        <span>Departure: <strong className="text-slate-300">{event.originLocation}</strong></span>
                                        <span>Arrival: <strong className="text-slate-300">{event.targetLocation}</strong></span>
                                        <span>Time Delta: <strong className="text-rose-400">{event.timeDifferenceMinutes} minutes</strong></span>
                                        <span>
                                            IP: <AbuseIPDBPopover ip={event.targetIp} showBadge={true} className="text-blue-400 hover:underline">{event.targetIp}</AbuseIPDBPopover>
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleRemediate(event.user, "Enforce Step-Up MFA")}
                                    className="px-3.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold font-mono transition-all shrink-0"
                                >
                                    Require Step-Up MFA
                                </button>
                            </div>
                        )))}
                    </div>
                </div>
            )}

            {/* TAB 2: RISKY USERS ROSTER */}
            {activeTab === 'users' && (
                <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/40">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono">
                            Enterprise Risky Users Roster
                        </span>
                        <span className="text-[10px] font-mono text-slate-400">Continuous Risk Evaluation</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                    <th className="px-4 py-3">User Account</th>
                                    <th className="px-4 py-3">Risk Level</th>
                                    <th className="px-4 py-3">Detection Reason</th>
                                    <th className="px-4 py-3">Login Failures</th>
                                    <th className="px-4 py-3">Last Known Location</th>
                                    <th className="px-4 py-3">Last Client IP</th>
                                    <th className="px-4 py-3 text-right">Autonomous Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {(data?.riskyUsers || []).map((u, idx) => (
                                    <tr key={idx} className="hover:bg-purple-600/[0.03] transition-colors">
                                        <td className="px-4 py-3 font-semibold text-slate-200">
                                            {u.user}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                u.riskLevel === 'High' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                                u.riskLevel === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                                'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                            }`}>
                                                {u.riskLevel}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">
                                            {u.reason}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-slate-300">
                                            {u.failureCount}
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 font-mono">
                                            {u.lastLocation}
                                        </td>
                                        <td className="px-4 py-3 font-mono">
                                            <AbuseIPDBPopover ip={u.lastIp} showBadge={true} className="text-blue-400 hover:underline">
                                                {u.lastIp}
                                            </AbuseIPDBPopover>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => handleRemediate(u.user, u.recommendedAction)}
                                                className="px-2.5 py-1 rounded bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.08] text-[10.5px] font-bold font-mono text-purple-300 transition-all"
                                            >
                                                {u.riskLevel === 'High' ? 'Revoke Tokens' : 'Step-Up MFA'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 3: SIGN-IN LOGS */}
            {activeTab === 'signins' && (
                <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden space-y-3">
                    <div className="p-4 border-b border-white/[0.06] flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#11192e]/40">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono">
                                Interactive Sign-in Stream
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/[0.06] text-slate-400 font-mono">
                                {filteredSignIns.length} Records
                            </span>
                        </div>

                        <div className="flex items-center gap-2.5">
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Filter by user, IP, or location..."
                                    value={signInSearch}
                                    onChange={(e) => setSignInSearch(e.target.value)}
                                    className="pl-9 pr-3 py-1.5 bg-[#070b14] border border-white/[0.1] rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500 w-60"
                                />
                            </div>

                            <div className="flex items-center p-0.5 bg-[#070b14] border border-white/[0.1] rounded-xl text-xs">
                                {['all', 'failed', 'success'].map(s => (
                                    <button
                                        key={s}
                                        onClick={() => setStatusFilter(s)}
                                        className={`px-3 py-1 rounded-lg text-[10.5px] font-bold uppercase font-mono transition-all ${
                                            statusFilter === s ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                    <th className="px-4 py-3">Time</th>
                                    <th className="px-4 py-3">User</th>
                                    <th className="px-4 py-3">Status / Error Code</th>
                                    <th className="px-4 py-3">Client IP (AbuseIPDB &amp; VT)</th>
                                    <th className="px-4 py-3">Location</th>
                                    <th className="px-4 py-3">Device / App</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {filteredSignIns.map((s, idx) => (
                                    <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                                        <td className="px-4 py-3 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                                            {s.time ? new Date(s.time).toLocaleTimeString() : 'Recent'}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-slate-200">
                                            {s.user || s.email}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                                {s.type || "Authentication Failure"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-mono">
                                            <AbuseIPDBPopover ip={s.ip} showBadge={true} className="text-blue-400 hover:underline">
                                                {s.ip}
                                            </AbuseIPDBPopover>
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 font-mono">
                                            {s.location || "Unknown"}
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 truncate max-w-[200px]">
                                            {s.os || s.clientApp || "Web Browser"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 4: DIRECTORY AUDIT TRAIL */}
            {activeTab === 'audits' && (
                <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/40">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono">
                            Microsoft Entra ID Security Administration Audit Trail
                        </span>
                        <span className="text-[10px] font-mono text-slate-400">Recent Directory Mutating Events</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                    <th className="px-4 py-3">Date &amp; Time</th>
                                    <th className="px-4 py-3">Activity</th>
                                    <th className="px-4 py-3">Category</th>
                                    <th className="px-4 py-3">Initiated By</th>
                                    <th className="px-4 py-3">Target Resource</th>
                                    <th className="px-4 py-3 text-right">Result</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {(data?.auditTrail || []).map((audit, idx) => (
                                    <tr key={idx} className="hover:bg-white/[0.02] transition-colors font-mono text-[11px]">
                                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                                            {new Date(audit.activityDateTime).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-slate-200 font-sans">
                                            {audit.activity}
                                        </td>
                                        <td className="px-4 py-3 text-purple-400">
                                            {audit.category}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">
                                            {audit.initiatedBy}
                                        </td>
                                        <td className="px-4 py-3 text-slate-400">
                                            {(audit.targetResources || []).join(", ")}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                                {audit.result}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
